// React
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

// Material-UI
import { Box, Container, Typography } from '@material-ui/core'

// React Final Form
import { Form } from 'react-final-form'

// Components
import ResearchDetail from '../component/web/shareUI/ResearchDetail/researchDetail'
import ResearcherDetail from '../component/web/shareUI/researcherDetail/researcherDetail'
import PreviewUploadDoc from '../component/web/shareUI/uploadDoc/PreviewUploadDoc'
import MyAccordionCtrl from '../control/accordion/accordion'
import MyRadio from '../control/radio/radio'
import MyTextfield from '../control/textfield/textfield'
import MyButton from '../control/button/button'
import PreviewUploadAdditionalDoc from '../component/web/shareUI/uploadAdditionalDoc/PreviewUploadAdditionalDoc'

// Hooks
import useTranslate from '../i18n/useTranslate'
import useLoading from '../control/loading/useLoading'
import useSnackbarAction from '../stores/snackbar/snackbarAction'
import useSubmissionApproval from '../services/um/useSubmissionApproval'

// Utilities
import { decryptionAES } from '../utilities/encryption'

// Types
import type { GetProjectResultType as PublicProjectInfo } from '../services/web/types/responseWebServiceTypes'

const SubmissionApproval: React.FC = () => {
    const query = new URLSearchParams(useLocation().search)

    const encryptProjectID = query.get('projectID')
    const encryptSubmittedID = query.get('submittedID')
    const token = query.get('token')

    const projectID = decryptionAES(encryptProjectID as string)
    const submittedID = decryptionAES(encryptSubmittedID as string)

    const { getPublicProjectInfo, advisorVerifyProject } = useSubmissionApproval()

    const snackbarAction = useSnackbarAction()
    const loadingHook = useLoading()
    const translate = useTranslate()

    const [project, setProject] = useState<PublicProjectInfo | null>(null)
    const [error, setError] = useState<Error | null>(null)
    const [isCompleted, setIsCompleted] = useState(false)

    const isApprovedOptions = useMemo(
        () => [
            {
                id: '1',
                name: translate.get('LABEL.APPROVE_SUBMISSION'),
            },
            {
                id: '2',
                name: translate.get('LABEL.REJECT_SUBMISSION'),
            },
        ],
        [],
    )

    const hasUploadAdditionalDocument = project?.documentAdditionalInfo && project.documentAdditionalInfo.length > 0

    const initialValues = {
        token: token as string,
        projectID: Number(projectID),
        submittedID: Number(submittedID),
        isApproved: undefined,
        remark: '',
    }

    const onSubmit = async (values: any) => {
        try {
            loadingHook.loadingOpen()

            const params = {
                ...values,
                isApproved: values.isApproved === '1',
            }

            const { status, message } = await advisorVerifyProject(params)

            if (status !== 1) {
                throw new Error(`submitted advisor verify project error with status: ${status} ${message}`)
            }

            setIsCompleted(true)
            snackbarAction.openSnackbar({
                message: translate.get('ALERT.CONTENT.SAVE_SUCCESS'),
                type: 'success',
            })
        } catch (error: any) {
            snackbarAction.openSnackbar({
                message: error.message,
                type: 'error',
            })
            console.error('has an error while submitting advisor verify project:', error)
        } finally {
            loadingHook.loadingClose()
        }
    }

    useEffect(() => {
        if (!projectID || !submittedID || !token) return

        const fetchProject = async () => {
            try {
                loadingHook.loadingOpen()
                const { result, status, message } = await getPublicProjectInfo(projectID, submittedID, { token })

                if (status !== 1) {
                    throw new Error(`fetched public project information error with status: ${status} ${message}`)
                }

                setProject(result)
            } catch (error) {
                setError(error as Error)
                console.error('has an error while fetching public project information:', error)
            } finally {
                loadingHook.loadingClose()
            }
        }

        fetchProject()
    }, [projectID, submittedID, token])

    return (
        <Container>
            {project ? (
                <Form
                    onSubmit={onSubmit}
                    initialValues={initialValues}
                    keepDirtyOnReinitialize={true}
                    render={({ handleSubmit, form, values }) => (
                        <form onSubmit={handleSubmit}>
                            <Box display="flex" flexDirection="column" style={{ gap: 16, paddingBlock: 32 }}>
                                <Box>
                                    <ResearchDetail dataSource={project} />
                                </Box>
                                <Box>
                                    <ResearcherDetail dataSource={project} />
                                </Box>
                                <Box>
                                    <PreviewUploadDoc dataSource={project} />
                                </Box>

                                {hasUploadAdditionalDocument ? (
                                    <Box>
                                        <PreviewUploadAdditionalDoc
                                            documentAdditionalInfo={project.documentAdditionalInfo}
                                        />
                                    </Box>
                                ) : null}
                                <Box>
                                    <MyAccordionCtrl title={translate.get('LABEL.APPROVAL_RESULT')}>
                                        <Box display="flex" flexDirection="column" style={{ gap: 16 }} width="100%">
                                            <MyRadio
                                                name="isApproved"
                                                options={isApprovedOptions}
                                                requiredHalfWidth={true}
                                                disabled={isCompleted}
                                                onChangeValue={() => form.change('remark', '')}
                                            />
                                            {values.isApproved === '2' ? (
                                                <MyTextfield
                                                    name="remark"
                                                    required={true}
                                                    materialInputProps={{
                                                        label: translate.get('LABEL.REMARK'),
                                                        disabled: isCompleted,
                                                        multiline: true,
                                                        rows: 5,
                                                    }}
                                                    maxLength={1000}
                                                />
                                            ) : null}
                                        </Box>
                                    </MyAccordionCtrl>
                                </Box>
                                <Box display="flex" justifyContent="flex-end">
                                    <MyButton
                                        type="submit"
                                        size="large"
                                        variant="contained"
                                        color="primary"
                                        disabled={isCompleted}
                                    >
                                        {translate.get('BUTTON.SAVE')}
                                    </MyButton>
                                </Box>
                            </Box>
                        </form>
                    )}
                />
            ) : error ? (
                <Box
                    width="100%"
                    height="100vh"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    textAlign="center"
                >
                    <Typography>
                        ไม่พบการยื่นที่ต้องอนุมัติ <br /> การยื่นอาจถูกอนุมัติแล้ว หรือ ลิงก์อาจไม่ถูกต้อง
                    </Typography>
                </Box>
            ) : (
                <></>
            )}
        </Container>
    )
}

export default SubmissionApproval
